<template>
  <div class="flex h-full w-full flex-col" :class="containerSize">
    <div
      v-if="data.byline && data.image?.byline"
      class="self-end px-2 py-2 font-sans text-xs lg:px-3 lg:py-3 lg:text-sm"
      :class="[
        data?.byline === 'bottom' && 'order-last',
        useDarkMode ? 'text-white' : 'text-black',
        imageMargins,
      ]"
    >
      {{ data.image?.byline }}
    </div>
    <div
      v-if="data?.image?.url"
      class="relative aspect-[343/230] overflow-hidden"
      :class="[imageSize, imageMargins]"
    >
      <ParallaxBgWrapper
        :wrapperClass="['relative aspect-[343/230] w-full overflow-hidden', imageSize]"
        :field_parallax_effect="data.field_parallax_effect"
      >
        <NuxtImg
          v-if="data?.image?.url"
          :src="data?.image?.url"
          :alt="data?.image?.alt"
          :provider="data?.provider || 'cloudinary'"
          loading="lazy"
          class="absolute h-full w-full object-cover object-center"
          :sizes="sizes"
          :width="size.width"
          :height="size.height"
          :fit="data.originalAspect ? 'inside' : 'fill'"
        />
      </ParallaxBgWrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
// Sibling: packages/astro-ui/src/components/DynamicBlocks/ImageBlock.astro
import { computed, inject, ref, type Ref } from 'vue'
import NuxtImg from '../atoms/NuxtImg.vue'
import type { IImageData } from '../../types'
import ParallaxBgWrapper from '../atoms/ParallaxBgWrapper.vue'

export interface IImageBlock {
  id: string
  image?: IImageData
  provider?: string
  desktopSize?: string
  byline?: string
  originalAspect?: boolean
  field_parallax_effect?: boolean
}

interface Props {
  data: IImageBlock
}

const props = defineProps<Props>()
const settings = inject<Ref<string[]>>('settings', ref([]))
const useDarkMode = computed(() => settings.value?.includes('dark_theme') ?? false)

const imageSize = computed<string>(() => {
  switch (props.data?.desktopSize) {
    case 'extra_wide':
      return props.data?.originalAspect
        ? '3xl:rounded-[1rem]'
        : 'xl:aspect-[1920/750] 3xl:rounded-[1rem]'
    case 'wide':
      return props.data?.originalAspect ? 'lg:rounded-2xl' : 'xl:aspect-[1536/500] lg:rounded-2xl'
    case 'small':
      return props.data?.originalAspect ? 'lg:rounded-2xl' : 'xl:aspect-[650/365] lg:rounded-2xl'
    default:
      return props.data?.originalAspect ? '' : 'xl:aspect-[1920/750]'
  }
})

// Frame and byline only: the parallax slot sits inside the frame and would be offset twice.
const imageMargins = computed(() =>
  props.data?.desktopSize === 'wide' || props.data?.desktopSize === 'small'
    ? 'lg:mx-4 2xl:mx-0'
    : '',
)

const size = computed<{ width: number; height?: number }>(() => {
  switch (props.data?.desktopSize) {
    case 'extra_wide':
      return props.data?.originalAspect ? { width: 1920 } : { width: 1920, height: 750 }
    case 'wide':
      return props.data?.originalAspect ? { width: 1536 } : { width: 1536, height: 500 }
    case 'small':
      return props.data?.originalAspect ? { width: 650 } : { width: 650, height: 365 }
    default:
      return props.data?.originalAspect ? { width: 1920 } : { width: 1920, height: 750 }
  }
})

const sizes = computed(() => {
  switch (props.data?.desktopSize) {
    case 'extra_wide':
      return 'xs:100vw sm:100vw md:100vw lg:100vw xl:1920px'
    case 'wide':
      return 'xs:100vw sm:100vw md:100vw lg:100vw xl:1260px'
    case 'small':
      return 'xs:100vw sm:100vw md:100vw lg:100vw xl:650px'
    default:
      return 'xs:100vw sm:100vw md:100vw lg:100vw xl:1260px'
  }
})

const containerSize = computed<string>(() => {
  switch (props.data?.desktopSize) {
    case 'extra_wide':
      return 'xl:max-w-[1920px]'
    case 'wide':
      return 'xl:max-w-[1536px]'
    case 'small':
      return 'xl:max-w-[650px]'
    default:
      return 'xl:max-w-[1920px]'
  }
})
</script>
